import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import communityFeedService from "./communityFeedService";
import { toast } from "react-toastify";

/**
 * Query keys for feed-related queries
 */
export const feedKeys = {
  all: ["communityFeed"],
  unified: (sort, filter, tags, time) => [
    "communityFeed",
    "unified",
    sort,
    filter,
    tags,
    time,
  ],
  popular: (time) => ["communityFeed", "popular", time],
  trending: () => ["communityFeed", "trending"],
  stats: () => ["communityFeed", "stats"],
  saved: (collection) => ["communityFeed", "saved", collection],
  savedIds: () => ["communityFeed", "savedIds"],
  collections: () => ["communityFeed", "collections"],
};

/**
 * Hook to fetch unified feed with infinite scroll
 * @param {string} sort - Sorting algorithm
 * @param {string} filter - Feed filter
 * @param {string} tags - Tag filter
 * @param {string} time - Time period for top/controversial
 * @returns {Object} - React Query infinite query result
 */
export const useUnifiedFeed = (
  sort = "hot",
  filter = "joined",
  tags = "",
  time = "week"
) => {
  return useInfiniteQuery({
    queryKey: feedKeys.unified(sort, filter, tags, time),
    queryFn: async ({ pageParam = null }) => {
      const response = await communityFeedService.getUnifiedFeed({
        sort,
        filter,
        tags,
        time,
        limit: 20,
        cursor: pageParam,
      });

      // Backend returns: { success: true, data: { threads: [...], pagination: {...}, meta: {...} } }
      // We need to extract data (axios .data already unwraps outer layer)
      const feedData = response?.data || response;

      // Return normalized structure for React Query
      return {
        threads: feedData?.threads || [],
        hasMore: feedData?.pagination?.hasMore || false,
        nextCursor: feedData?.pagination?.cursor || null,
        algorithm: feedData?.meta?.algorithm,
        totalCommunities: feedData?.meta?.totalCommunities,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.hasMore ? lastPage?.nextCursor : undefined;
    },
    staleTime:
      sort === "hot"
        ? 2 * 60 * 1000
        : sort === "new" || sort === "rising"
        ? 3 * 60 * 1000
        : 5 * 60 * 1000, // 2-5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch popular feed
 * @param {string} time - Time period
 * @param {number} limit - Number of threads
 * @returns {Object} - React Query result
 */
export const usePopularFeed = (time = "day", limit = 20) => {
  return useQuery({
    queryKey: feedKeys.popular(time),
    queryFn: async () => {
      const response = await communityFeedService.getPopularFeed(time, limit);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000,
  });
};

/**
 * Hook to fetch trending feed
 * @param {number} limit - Number of threads
 * @returns {Object} - React Query result
 */
export const useTrendingFeed = (limit = 20) => {
  return useQuery({
    queryKey: feedKeys.trending(),
    queryFn: async () => {
      const response = await communityFeedService.getTrendingFeed(limit);
      return response.data;
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
    cacheTime: 30 * 60 * 1000,
  });
};

/**
 * Hook to fetch feed stats
 * @returns {Object} - React Query result
 */
export const useFeedStats = () => {
  return useQuery({
    queryKey: feedKeys.stats(),
    queryFn: async () => {
      const response = await communityFeedService.getFeedStats();
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to vote on thread with optimistic updates
 * @returns {Object} - React Query mutation
 */
export const useThreadVote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ communityId, threadId }) => {
      return await communityFeedService.voteThread(communityId, threadId);
    },
    onMutate: async ({ threadId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: feedKeys.all });

      // Snapshot previous values
      const previousFeeds = queryClient.getQueriesData({
        queryKey: feedKeys.all,
      });

      // Optimistically update all feed queries
      queryClient.setQueriesData({ queryKey: feedKeys.all }, (old) => {
        if (!old) return old;

        // Handle infinite query structure
        if (old.pages) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              threads: page.threads.map((thread) => {
                if (thread._id === threadId) {
                  const wasUpvoted = thread.userHasUpvoted;
                  const wasDownvoted = thread.userHasDownvoted;

                  let newScore = thread.score;
                  let newUpvoteCount = thread.upvoteCount;
                  let newDownvoteCount = thread.downvoteCount;

                  if (wasUpvoted) {
                    // Remove upvote
                    newScore -= 1;
                    newUpvoteCount -= 1;
                  } else if (wasDownvoted) {
                    // Switch from downvote to upvote
                    newScore += 2;
                    newUpvoteCount += 1;
                    newDownvoteCount -= 1;
                  } else {
                    // Add upvote
                    newScore += 1;
                    newUpvoteCount += 1;
                  }

                  return {
                    ...thread,
                    userHasUpvoted: !wasUpvoted,
                    userHasDownvoted: false,
                    score: newScore,
                    upvoteCount: newUpvoteCount,
                    downvoteCount: newDownvoteCount,
                  };
                }
                return thread;
              }),
            })),
          };
        }

        // Handle regular query structure
        if (old.threads) {
          return {
            ...old,
            threads: old.threads.map((thread) => {
              if (thread._id === threadId) {
                const wasUpvoted = thread.userHasUpvoted;
                const wasDownvoted = thread.userHasDownvoted;

                let newScore = thread.score;
                let newUpvoteCount = thread.upvoteCount;
                let newDownvoteCount = thread.downvoteCount;

                if (wasUpvoted) {
                  newScore -= 1;
                  newUpvoteCount -= 1;
                } else if (wasDownvoted) {
                  newScore += 2;
                  newUpvoteCount += 1;
                  newDownvoteCount -= 1;
                } else {
                  newScore += 1;
                  newUpvoteCount += 1;
                }

                return {
                  ...thread,
                  userHasUpvoted: !wasUpvoted,
                  userHasDownvoted: false,
                  score: newScore,
                  upvoteCount: newUpvoteCount,
                  downvoteCount: newDownvoteCount,
                };
              }
              return thread;
            }),
          };
        }

        return old;
      });

      return { previousFeeds };
    },
    onError: (err, variables, context) => {
      // Revert optimistic updates on error
      if (context?.previousFeeds) {
        context.previousFeeds.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error("Failed to vote on thread");
},
    onSuccess: (data) => {
      // Optionally show success feedback
      // toast.success('Vote updated');
    },
  });
};

/**
 * Hook to downvote on thread with optimistic updates
 * @returns {Object} - React Query mutation
 */
export const useThreadDownvote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ communityId, threadId }) => {
      return await communityFeedService.downvoteThread(communityId, threadId);
    },
    onMutate: async ({ threadId }) => {
      await queryClient.cancelQueries({ queryKey: feedKeys.all });
      const previousFeeds = queryClient.getQueriesData({
        queryKey: feedKeys.all,
      });

      queryClient.setQueriesData({ queryKey: feedKeys.all }, (old) => {
        if (!old) return old;

        // Handle infinite query structure
        if (old.pages) {
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              threads: page.threads.map((thread) => {
                if (thread._id === threadId) {
                  const wasUpvoted = thread.userHasUpvoted;
                  const wasDownvoted = thread.userHasDownvoted;

                  let newScore = thread.score;
                  let newUpvoteCount = thread.upvoteCount;
                  let newDownvoteCount = thread.downvoteCount;

                  if (wasDownvoted) {
                    // Remove downvote
                    newScore += 1;
                    newDownvoteCount -= 1;
                  } else if (wasUpvoted) {
                    // Switch from upvote to downvote
                    newScore -= 2;
                    newUpvoteCount -= 1;
                    newDownvoteCount += 1;
                  } else {
                    // Add downvote
                    newScore -= 1;
                    newDownvoteCount += 1;
                  }

                  return {
                    ...thread,
                    userHasUpvoted: false,
                    userHasDownvoted: !wasDownvoted,
                    score: newScore,
                    upvoteCount: newUpvoteCount,
                    downvoteCount: newDownvoteCount,
                  };
                }
                return thread;
              }),
            })),
          };
        }

        // Handle regular query structure
        if (old.threads) {
          return {
            ...old,
            threads: old.threads.map((thread) => {
              if (thread._id === threadId) {
                const wasUpvoted = thread.userHasUpvoted;
                const wasDownvoted = thread.userHasDownvoted;

                let newScore = thread.score;
                let newUpvoteCount = thread.upvoteCount;
                let newDownvoteCount = thread.downvoteCount;

                if (wasDownvoted) {
                  newScore += 1;
                  newDownvoteCount -= 1;
                } else if (wasUpvoted) {
                  newScore -= 2;
                  newUpvoteCount -= 1;
                  newDownvoteCount += 1;
                } else {
                  newScore -= 1;
                  newDownvoteCount += 1;
                }

                return {
                  ...thread,
                  userHasUpvoted: false,
                  userHasDownvoted: !wasDownvoted,
                  score: newScore,
                  upvoteCount: newUpvoteCount,
                  downvoteCount: newDownvoteCount,
                };
              }
              return thread;
            }),
          };
        }

        return old;
      });

      return { previousFeeds };
    },
    onError: (err, variables, context) => {
      if (context?.previousFeeds) {
        context.previousFeeds.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error("Failed to downvote thread");
},
  });
};

/**
 * Hook to save thread
 * @returns {Object} - React Query mutation
 */
export const useSaveThread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadId, collection, tags }) => {
      return await communityFeedService.saveThread(threadId, collection, tags);
    },
    onSuccess: () => {
      // Invalidate ALL saved queries (any collection)
      queryClient.invalidateQueries({ queryKey: ["communityFeed", "saved"] });
      queryClient.invalidateQueries({ queryKey: feedKeys.savedIds() });
      queryClient.invalidateQueries({ queryKey: feedKeys.collections() });
      toast.success("Thread saved successfully");
    },
    onError: (err) => {
      toast.error("Failed to save thread");
},
  });
};

/**
 * Hook to unsave thread
 * @returns {Object} - React Query mutation
 */
export const useUnsaveThread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadId }) => {
      return await communityFeedService.unsaveThread(threadId);
    },
    onSuccess: () => {
      // Invalidate ALL saved queries (any collection)
      queryClient.invalidateQueries({ queryKey: ["communityFeed", "saved"] });
      queryClient.invalidateQueries({ queryKey: feedKeys.savedIds() });
      queryClient.invalidateQueries({ queryKey: feedKeys.collections() });
      toast.success("Thread removed from saved");
    },
    onError: (err) => {
      toast.error("Failed to unsave thread");
},
  });
};

/**
 * Hook to fetch saved threads
 * @param {string} collection - Collection filter
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Object} - React Query result
 */
export const useSavedThreads = (collection = null, page = 1, limit = 20) => {
  return useQuery({
    queryKey: feedKeys.saved(collection),
    queryFn: async () => {
      const response = await communityFeedService.getSavedThreads(
        collection,
        page,
        limit
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch collections
 * @returns {Object} - React Query result
 */
export const useCollections = () => {
  return useQuery({
    queryKey: feedKeys.collections(),
    queryFn: async () => {
      const response = await communityFeedService.getCollections();
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch all saved thread IDs for local caching
 * Call this once on app load for instant save status checks
 * @returns {Object} - React Query result with Set<string> of thread IDs
 */
export const useSavedThreadIds = () => {
  return useQuery({
    queryKey: feedKeys.savedIds(),
    queryFn: async () => {
      const response = await communityFeedService.getSavedThreadIds();
      // API returns { success: true, data: { threadIds: [...], count: N } }
      const threadIds = response.data?.threadIds || [];
      return {
        savedSet: new Set(threadIds),
        count: response.data?.count || threadIds.length,
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (renamed from cacheTime in v5)
  });
};
